import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Puzzle, Save, CheckCircle2, XCircle, ExternalLink, Key } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntegrationsPage = () => {
  const [integrations, setIntegrations] = useState({
    // Microsoft 365
    microsoft_enabled: false,
    microsoft_tenant_id: '',
    microsoft_client_id: '',
    microsoft_sso_enabled: false,
    microsoft_email_enabled: false,
    microsoft_calendar_enabled: false,
    
    // RingCentral
    ringcentral_enabled: true,
    ringcentral_client_id: '',
    ringcentral_jwt_token: '',
    ringcentral_webhook_url: '',
    
    // Jotform
    jotform_enabled: false,
    jotform_api_key: '',
    
    // OpenAI (via Emergent)
    openai_enabled: true,
    openai_model: 'gpt-4o'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingIntegration, setTestingIntegration] = useState(null);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/admin/integrations`);
      if (response.data) {
        setIntegrations(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveIntegrations = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/integrations`, integrations);
      toast.success('Integration settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testIntegration = async (integrationName) => {
    setTestingIntegration(integrationName);
    try {
      const response = await axios.post(`${API}/admin/integrations/test/${integrationName}`);
      if (response.data.success) {
        toast.success(`${integrationName} connection successful!`);
      } else {
        toast.error(`${integrationName} connection failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error(`Failed to test ${integrationName}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setTestingIntegration(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Puzzle className="w-6 h-6" />
              System Integrations
            </h1>
            <p className="text-muted-foreground">Configure third-party service connections</p>
          </div>
        </div>
        <Button onClick={saveIntegrations} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Microsoft 365 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.5 2H2v9.5h9.5V2zm1 0v9.5H22V2H12.5zM2 12.5V22h9.5v-9.5H2zm10.5 0V22H22v-9.5H12.5z"/>
                  </svg>
                </div>
                <div>
                  <CardTitle>Microsoft 365</CardTitle>
                  <CardDescription>Azure AD SSO, Email, Calendar integration</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {integrations.microsoft_enabled ? (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    <XCircle className="w-3 h-3 mr-1" />
                    Not Configured
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Microsoft 365</p>
                <p className="text-sm text-muted-foreground">Connect to Microsoft services</p>
              </div>
              <Switch
                checked={integrations.microsoft_enabled}
                onCheckedChange={(v) => setIntegrations({ ...integrations, microsoft_enabled: v })}
              />
            </div>

            {integrations.microsoft_enabled && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Tenant ID</Label>
                    <Input
                      type="password"
                      value={integrations.microsoft_tenant_id}
                      onChange={(e) => setIntegrations({ ...integrations, microsoft_tenant_id: e.target.value })}
                      placeholder="Enter Azure AD Tenant ID"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input
                      type="password"
                      value={integrations.microsoft_client_id}
                      onChange={(e) => setIntegrations({ ...integrations, microsoft_client_id: e.target.value })}
                      placeholder="Enter Application Client ID"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={integrations.microsoft_sso_enabled}
                      onCheckedChange={(v) => setIntegrations({ ...integrations, microsoft_sso_enabled: v })}
                    />
                    <span className="text-sm">SSO Login</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={integrations.microsoft_email_enabled}
                      onCheckedChange={(v) => setIntegrations({ ...integrations, microsoft_email_enabled: v })}
                    />
                    <span className="text-sm">Email (Graph API)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={integrations.microsoft_calendar_enabled}
                      onCheckedChange={(v) => setIntegrations({ ...integrations, microsoft_calendar_enabled: v })}
                    />
                    <span className="text-sm">Calendar Sync</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testIntegration('microsoft')}
                  disabled={testingIntegration === 'microsoft'}
                >
                  {testingIntegration === 'microsoft' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Test Connection
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* RingCentral */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                </div>
                <div>
                  <CardTitle>RingCentral</CardTitle>
                  <CardDescription>SMS, Voice calls, and messaging</CardDescription>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-900/10">
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">RingCentral is Active</p>
                <p className="text-sm text-green-700 dark:text-green-400">SMS and voice services are connected</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/outbound-numbers">
                  Manage Numbers
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/webhook-diagnostics">
                  Webhook Diagnostics
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jotform */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                  </svg>
                </div>
                <div>
                  <CardTitle>Jotform</CardTitle>
                  <CardDescription>Online forms and document templates</CardDescription>
                </div>
              </div>
              {integrations.jotform_enabled ? (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Configured
                </Badge>
              ) : (
                <Badge variant="outline">
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Configured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Jotform</p>
                <p className="text-sm text-muted-foreground">Connect to Jotform for form submissions</p>
              </div>
              <Switch
                checked={integrations.jotform_enabled}
                onCheckedChange={(v) => setIntegrations({ ...integrations, jotform_enabled: v })}
              />
            </div>

            {integrations.jotform_enabled && (
              <>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={integrations.jotform_api_key}
                    onChange={(e) => setIntegrations({ ...integrations, jotform_api_key: e.target.value })}
                    placeholder="Enter Jotform API Key"
                  />
                  <p className="text-xs text-muted-foreground">
                    Get your API key from{' '}
                    <a href="https://www.jotform.com/myaccount/api" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Jotform Settings <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </p>
                </div>

                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => testIntegration('jotform')}
                  disabled={testingIntegration === 'jotform'}
                >
                  {testingIntegration === 'jotform' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Test Connection
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* OpenAI */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <CardTitle>OpenAI (via Emergent)</CardTitle>
                  <CardDescription>AI-powered features using GPT models</CardDescription>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-900/10">
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">AI Services Active</p>
                <p className="text-sm text-green-700 dark:text-green-400">Powered by Emergent Universal Key</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>

            <div className="space-y-2">
              <Label>AI Model</Label>
              <Select
                value={integrations.openai_model}
                onValueChange={(v) => setIntegrations({ ...integrations, openai_model: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o (Recommended)</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Faster)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/ai-settings">
                Configure AI Settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntegrationsPage;
